import { GetStaffQueryFilters, StaffArrangeBy } from '@schooly/api';
import {
  AgeGroupSelectMultiple,
  ArrangeBySelect,
  DateSelect,
  FiltersSidebar,
  FiltersSidebarMethods,
  GenderSelectMultiple,
  NationalitySelectMultiple,
  PropertyTypeSelectMultiple,
  SubjectSelectMultiple,
  toggleMultipleValueArrayProperty,
  useFiltersSidebar,
} from '@schooly/components/filters';
import { SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { forwardRef, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { useStaffFilters } from './useStaffFilters';

type StaffFiltersSidebarProps = {
  filters: GetStaffQueryFilters;
  arrangeBy: StaffArrangeBy | null;
  defaultFilters: GetStaffQueryFilters;
  schoolId: string;
  onApply: (v: { filters: GetStaffQueryFilters; arrangeBy: StaffArrangeBy | null }) => void;
};

export const StaffFiltersSidebar = forwardRef<FiltersSidebarMethods, StaffFiltersSidebarProps>(
  (
    { filters: propsFilters, defaultFilters, onApply, schoolId, arrangeBy: propsArrangeBy },
    ref,
  ) => {
    const [filters, setFilters] = useState(propsFilters);
    const [arrangeBy, setArrangeBy] = useState(propsArrangeBy);
    const { allowClose, preventClose, sidebar } = useFiltersSidebar(ref);
    const {
      labels,
      hasHouses,
      hasDepartments,
      filtersHouse,
      filtersGender,
      filtersNationality,
      filtersStatus,
      filtersAgeGroup,
      filtersDepartment,
      filtersSubject,
      onSetNationality,
      onClearNationality,
      onSetGender,
      onClearGender,
      onSetHouseId,
      onClearHouse,
      onSetStatusId,
      onClearStatus,
      onSetDepartmentId,
      onClearDepartment,
      onSetSubjectId,
      onClearSubject,
      arrangeByDropdownOptions,
      onSetDate,
      filtersDate,
      onSetAgeGroup,
      onClearAgeGroup,
    } = useStaffFilters({ schoolId, filters, onSetFilters: setFilters });
    const { formatMessage } = useIntl();

    const handleApply = useCallback(() => {
      onApply({ filters, arrangeBy });
    }, [filters, onApply, arrangeBy]);

    const handleClear = useCallback(() => {
      setFilters(defaultFilters);
      setArrangeBy(null);
    }, [defaultFilters]);

    return (
      <FiltersSidebar
        onOpen={() => {
          setFilters(propsFilters);
          setArrangeBy(propsArrangeBy);
        }}
        ref={sidebar}
        title={formatMessage(
          { id: 'filter-FiltersForPage' },
          { page: formatMessage({ id: 'section-Staff' }) },
        )}
        onClear={handleClear}
        onApply={handleApply}
      >
        <DateSelect
          onSetDate={onSetDate}
          date={filtersDate}
          placeholder={labels.date}
          requiredLabel="required"
          emptyValue={formatMessage({ id: 'datepicker-Today' })}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <PropertyTypeSelectMultiple
          placeholder={labels.status}
          allowArchived
          userRole={SchoolUserRole.Staff}
          schoolId={schoolId}
          onSelectId={(v: string) =>
            onSetStatusId(toggleMultipleValueArrayProperty(filtersStatus, [v]))
          }
          selectedIds={filtersStatus || []}
          onClear={onClearStatus}
          propertyType={SchoolPropertyType.Status}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <AgeGroupSelectMultiple
          allowArchived
          userRole={SchoolUserRole.Staff}
          placeholder={labels.age_group}
          schoolId={schoolId}
          onSelect={(v) => onSetAgeGroup(toggleMultipleValueArrayProperty(filtersAgeGroup, v))}
          selectedIds={filtersAgeGroup || []}
          onClear={onClearAgeGroup}
          onOpen={preventClose}
          onClose={allowClose}
        />

        {hasHouses && (
          <PropertyTypeSelectMultiple
            placeholder={labels.house}
            allowArchived
            userRole={SchoolUserRole.Staff}
            schoolId={schoolId}
            onSelectId={(v: string) =>
              onSetHouseId(toggleMultipleValueArrayProperty(filtersHouse, [v]))
            }
            selectedIds={filtersHouse || []}
            onClear={onClearHouse}
            propertyType={SchoolPropertyType.House}
            onOpen={preventClose}
            onClose={allowClose}
          />
        )}

        {hasDepartments && (
          <PropertyTypeSelectMultiple
            placeholder={labels.department}
            allowArchived
            userRole={SchoolUserRole.Staff}
            schoolId={schoolId}
            onSelectId={(v: string) =>
              onSetDepartmentId(toggleMultipleValueArrayProperty(filtersDepartment, [v]))
            }
            selectedIds={filtersDepartment || []}
            onClear={onClearDepartment}
            propertyType={SchoolPropertyType.Department}
            onOpen={preventClose}
            onClose={allowClose}
          />
        )}

        <SubjectSelectMultiple
          placeholder={labels.subject}
          schoolId={schoolId}
          onSelectSubjectId={(v: string) =>
            onSetSubjectId(toggleMultipleValueArrayProperty(filtersSubject, [v]))
          }
          selectedIds={filtersSubject || []}
          onClear={onClearSubject}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <GenderSelectMultiple
          placeholder={labels.gender}
          onSelectGender={(v) => onSetGender(toggleMultipleValueArrayProperty(filtersGender, [v]))}
          selectedGenders={filtersGender || []}
          onClear={onClearGender}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <NationalitySelectMultiple
          placeholder={labels.nationality}
          onSelectNationality={(v) =>
            onSetNationality(toggleMultipleValueArrayProperty(filtersNationality, [v]))
          }
          selectedNationalities={filtersNationality || []}
          onClear={onClearNationality}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <ArrangeBySelect
          options={arrangeByDropdownOptions}
          selectedOption={arrangeBy ?? undefined}
          placeholder={labels.arrangeBy}
          onClear={() => setArrangeBy(null)}
          onSelectOption={setArrangeBy}
          onOpen={preventClose}
          onClose={allowClose}
        />
      </FiltersSidebar>
    );
  },
);
